import { useRef, useState, useMemo, useEffect } from "react";
import { FormControlLabel, IconButton, Switch, Tooltip } from "@mui/material";
import styles from "./styles.module.scss";
import {
  WorkflowTemplate,
  WorkflowStep,
  StepAction,
  Assignee,
  WorkflowCondition,
  ConditionOperator,
  ApprovalMode,
  WORKFLOW_LS_KEY,
} from "./types";
import {
  docNameForSystemType,
  isEngineSupported,
  systemTypeForDocName,
} from "./documentsStore";
import { MAGISTRAT_ENGAGEMENT_STEPS } from "./engagementPreset";
import AddIcon from "assets/icons/add.svg?react";
import EditIcon from "assets/icons/edite.svg?react";
import DeleteIcon from "assets/icons/delete_24.svg?react";
import {
  getOrgChartPositions,
  getOrgChartPeople,
  membersToAssigneePeople,
  setAssigneePeople,
  OrgPersonEntry,
} from "./assigneePeople";
import workflowService from "api/workflowService";
import workspaceService from "api/workspaceService";
import { getCompanyId as getWorkspaceId } from "utils/getCompanyId";
import DocumentsCatalog from "./DocumentsCatalog";

// ── Stałe ────────────────────────────────────────────────────────

const ROLES: Record<string, { label: string; color: string; bg: string }> = {
  ACCOUNTANT: { label: "Księgowy", color: "#5d49c7", bg: "#ede8f8" },
  MANAGER: { label: "Kierownik", color: "#2e7d32", bg: "#e8f5e9" },
  DIRECTOR: { label: "Dyrektor", color: "#e65100", bg: "#fff3e0" },
  BOARD: { label: "Zarząd", color: "#b71c1c", bg: "#fce4ec" },
  FINANCE: { label: "Finanse", color: "#1565c0", bg: "#e3f2fd" },
  LEGAL: { label: "Prawnik", color: "#6a1b9a", bg: "#f3e5f5" },
};

const ACTION_LABELS: Record<StepAction, string> = {
  approve: "Zatwierdź",
  reject: "Odrzuć",
  complete: "Zakończ",
  send_back: "Zwróć",
};

const DOC_TYPE_LABELS: Record<string, string> = {
  engagement: "Zaangażowanie budżetowe",
  case: "Sprawa",
  task: "Działanie",
  letter: "Pismo / Dokument",
  incoming_package: "Przesyłka przychodząca",
  outgoing_package: "Przesyłka wychodząca",
  contract: "Umowa",
  invoice_purchase: "Faktura zakupowa",
  invoice_sale: "Faktura sprzedaży",
  bill: "Rachunek",
  employment_contract: "Umowa o pracę",
  employment_termination: "Rozwiązanie umowy o pracę",
  leave_request: "Wniosek urlopowy",
  bonus_request: "Wniosek premiowy",
  training_referral: "Skierowanie na szkolenie",
  rfq: "Zapytanie ofertowe",
  order: "Zamówienie",
  complaint: "Reklamacja",
  public_info_request: "Zapytanie w trybie dostępu do informacji publicznej",
  other: "Inny dokument",
};

const STEP_COLORS = [
  "#5d49c7",
  "#2e7d32",
  "#e65100",
  "#1565c0",
  "#6a1b9a",
  "#b71c1c",
];

const ASSIGNEE_COLORS = [
  "#5d49c7",
  "#2e7d32",
  "#e65100",
  "#1565c0",
  "#6a1b9a",
  "#b71c1c",
  "#00838f",
  "#558b2f",
];

// ── Dane demonstracyjne ───────────────────────────────────────────

const DEMO_TEMPLATES: WorkflowTemplate[] = [
  {
    id: "wf-1",
    name: "Zatwierdzanie faktury zakupowej",
    documentType: "invoice_purchase",
    description: "Weryfikacja i akceptacja faktur przed płatnością",
    isActive: true,
    steps: [
      {
        id: "s-1",
        order: 1,
        name: "Weryfikacja formalna i merytoryczna",
        description:
          "Sprawdzenie zgodności danych na fakturze z zamówieniem, umową oraz faktycznym wykonaniem usługi lub dostawą towaru",
        documentFormula:
          "Faktura zweryfikowana pod względem formalnym i merytorycznym. Zgodność z zamówieniem i umową potwierdzona.",
        role: "ACCOUNTANT",
        actions: ["approve", "reject"],
        conditions: [],
        approvalMode: "any",
        assignees: [
          {
            id: "u-1",
            name: "Inspektor ds. księgowości",
            initials: "AK",
            color: ASSIGNEE_COLORS[0],
          },
          {
            id: "u-2",
            name: "Główny Księgowy",
            initials: "PN",
            color: ASSIGNEE_COLORS[1],
          },
        ],
      },
      {
        id: "s-2",
        order: 2,
        name: "Akceptacja kwotowa",
        description:
          "Przekazanie do uprawnionej osoby w zależności od wartości faktury",
        documentFormula:
          "Faktura zaakceptowana kwotowo przez uprawnioną osobę zgodnie z obowiązującym regulaminem udzielania zamówień.",
        role: "MANAGER",
        actions: ["approve", "reject"],
        conditions: [
          { id: "c-1", operator: "lt", value: 5000, assignRole: "MANAGER" },
          {
            id: "c-2",
            operator: "between",
            value: [5000, 50000],
            assignRole: "DIRECTOR",
          },
          { id: "c-3", operator: "gte", value: 50000, assignRole: "BOARD" },
        ],
        approvalMode: "all",
        assignees: [
          {
            id: "u-3",
            name: "Skarbnik Gminy",
            initials: "MW",
            color: ASSIGNEE_COLORS[2],
          },
          {
            id: "u-4",
            name: "Sekretarz Gminy",
            initials: "KL",
            color: ASSIGNEE_COLORS[3],
          },
          {
            id: "u-5",
            name: "Wójt Gminy",
            initials: "TD",
            color: ASSIGNEE_COLORS[4],
          },
        ],
      },
      {
        id: "s-3",
        order: 3,
        name: "Dekretacja i księgowanie",
        description:
          "Przypisanie do konta i centrum kosztów, wprowadzenie do systemu ERP",
        documentFormula:
          "Faktura zadekretowana i wprowadzona do ewidencji księgowej.",
        role: "ACCOUNTANT",
        actions: ["complete", "reject"],
        conditions: [],
        approvalMode: "any",
        assignees: [
          {
            id: "u-1",
            name: "Inspektor ds. księgowości",
            initials: "AK",
            color: ASSIGNEE_COLORS[0],
          },
        ],
      },
      {
        id: "s-4",
        order: 4,
        name: "Płatność i archiwizacja",
        description:
          "Przelew w terminie płatności, zabezpieczenie dokumentu dla celów podatkowych",
        documentFormula:
          "Płatność zrealizowana w terminie. Dokument zarchiwizowany zgodnie z instrukcją kancelaryjną.",
        role: "FINANCE",
        actions: ["complete"],
        conditions: [],
        approvalMode: "any",
        assignees: [
          {
            id: "u-6",
            name: "Inspektor ds. budżetu",
            initials: "JZ",
            color: ASSIGNEE_COLORS[5],
          },
        ],
      },
    ],
  },
  {
    id: "wf-2",
    name: "Zatwierdzanie umowy",
    documentType: "contract",
    description: "Przegląd prawny i akceptacja umów przez zarząd",
    isActive: false,
    steps: [
      {
        id: "s-5",
        order: 1,
        name: "Przegląd prawny",
        description: "Weryfikacja postanowień umowy przez dział prawny",
        role: "LEGAL",
        actions: ["approve", "reject"],
        conditions: [],
        approvalMode: "all",
        assignees: [
          {
            id: "u-7",
            name: "Radca prawny",
            initials: "MK",
            color: ASSIGNEE_COLORS[6],
          },
          {
            id: "u-8",
            name: "Inspektor ds. zamówień publicznych",
            initials: "BS",
            color: ASSIGNEE_COLORS[7],
          },
        ],
      },
      {
        id: "s-6",
        order: 2,
        name: "Akceptacja zarządu",
        description: "Ostateczna akceptacja umowy przez zarząd",
        role: "BOARD",
        actions: ["approve", "reject"],
        conditions: [],
        approvalMode: "any",
        assignees: [
          {
            id: "u-3",
            name: "Skarbnik Gminy",
            initials: "MW",
            color: ASSIGNEE_COLORS[2],
          },
        ],
      },
    ],
  },
];

// ── LocalStorage ────────────────────────────────────────────────

const loadSavedTemplates = (): WorkflowTemplate[] | null => {
  try {
    const raw = localStorage.getItem(WORKFLOW_LS_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
  } catch {
    return null;
  }
};

// ── Konwertery do edycji etapu ───────────────────────────────────

const assigneesToOrgPeople = (assignees: Assignee[]): OrgPersonEntry[] => {
  const allPeople: OrgPersonEntry[] = [
    REQUESTER_ENTRY,
    DIRECT_SUPERVISOR_ENTRY as OrgPersonEntry,
    ...getOrgChartPositions(),
  ];
  return assignees
    .map((a) => allPeople.find((p) => p.id === a.id) ?? null)
    .filter(Boolean) as OrgPersonEntry[];
};

const conditionsToDrafts = (
  conditions: WorkflowCondition[],
): ConditionDraft[] =>
  conditions.map((c) => ({
    id: c.id,
    operator: c.operator,
    amount: Array.isArray(c.value) ? String(c.value[0]) : String(c.value),
    amountTo:
      c.operator === "between" && Array.isArray(c.value)
        ? String(c.value[1])
        : "",
    people: assigneesToOrgPeople(c.assignees ?? []),
    mode: c.conditionApprovalMode ?? "any",
  }));

// ── Pomocnicze ────────────────────────────────────────────────────

function formatConditionAmount(
  value: number | [number, number],
  operator: string,
): string {
  const fmt = (n: number) => n.toLocaleString("pl-PL") + " PLN";
  if (operator === "lt") return `poniżej ${fmt(value as number)}`;
  if (operator === "lte") return `do ${fmt(value as number)}`;
  if (operator === "gte") return `${fmt(value as number)} i powyżej`;
  if (operator === "gt") return `powyżej ${fmt(value as number)}`;
  if (operator === "between") {
    const [lo, hi] = value as [number, number];
    return `${fmt(lo)} – ${fmt(hi)}`;
  }
  return String(value);
}

// Stylizacja finansowa inputu kwoty (pl-PL: separatory tysięcy + 2 miejsca).
function formatFinancialAmount(raw: string): string {
  const n = parseFloat(
    String(raw ?? "")
      .replace(/\s/g, "")
      .replace(",", "."),
  );
  if (isNaN(n)) return "";
  return n.toLocaleString("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
const unformatAmount = (s: string): string =>
  String(s ?? "").replace(/\s/g, "");

// ── Komponent karty etapu ─────────────────────────────────────────

function StepCard({
  step,
  index,
  onFormulaChange,
  onEdit,
  onDelete,
}: {
  step: WorkflowStep;
  index: number;
  onFormulaChange: (stepId: string, formula: string) => void;
  onEdit: (stepId: string) => void;
  onDelete: (stepId: string) => void;
}) {
  const color = STEP_COLORS[index % STEP_COLORS.length];
  const approvalModeLabel =
    step.approvalMode === "two_or_more"
      ? "Co najmniej dwie osoby"
      : step.approvalMode === "all"
      ? step.assignees.length > 1
        ? "Wszyscy (kontrasygnata)"
        : "Jedna osoba"
      : "Dowolna osoba";

  // W wierszu Osoby prezentujemy STANOWISKO, nie imię i nazwisko.
  // Jeśli przypisanie zawiera nazwisko (dane historyczne), tłumaczymy je na
  // nazwę stanowiska wg struktury organizacyjnej.
  const personToPosition = useMemo(() => {
    const m: Record<string, string> = {};
    for (const p of getOrgChartPeople()) {
      if (p.personFullName) {
        m[p.personFullName] = p.unit ? `${p.position} — ${p.unit}` : p.position;
      }
      m[p.name] = p.unit ? `${p.position} — ${p.unit}` : p.position;
    }
    return m;
  }, []);
  const positionLabel = (a: Assignee) => personToPosition[a.name] ?? a.name;

  const [editingFormula, setEditingFormula] = useState(false);
  const [formulaDraft, setFormulaDraft] = useState(step.documentFormula ?? "");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const startEdit = () => {
    setFormulaDraft(step.documentFormula ?? "");
    setEditingFormula(true);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const saveFormula = () => {
    onFormulaChange(step.id, formulaDraft);
    setEditingFormula(false);
  };

  const cancelFormula = () => {
    setFormulaDraft(step.documentFormula ?? "");
    setEditingFormula(false);
  };

  return (
    <div className={styles.stepCard}>
      <div className={styles.stepCardAccent} style={{ background: color }} />
      <div className={styles.stepCardBody}>
        <div className={styles.stepCardHeader}>
          <div className={styles.stepNumber} style={{ background: color }}>
            {step.order}
          </div>

          <div className={styles.stepCardTitle}>
            <h3>{step.name}</h3>
            {step.description && <p>{step.description}</p>}
          </div>

          <div className={styles.stepCardControls}>
            <Tooltip title="Edytuj etap">
              <IconButton size="small" onClick={() => onEdit(step.id)}>
                <EditIcon style={{ width: 16, height: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Usuń etap">
              <IconButton size="small" onClick={() => onDelete(step.id)}>
                <DeleteIcon style={{ width: 16, height: 16 }} />
              </IconButton>
            </Tooltip>
          </div>
        </div>

        {/* Sekcja formuły dokumentacyjnej */}
        <div className={styles.formulaSection}>
          <div className={styles.formulaLabel}>Formuła dokumentacyjna</div>
          {editingFormula ? (
            <div className={styles.formulaEditBox}>
              <textarea
                ref={textareaRef}
                className={styles.formulaTextarea}
                value={formulaDraft}
                onChange={(e) => setFormulaDraft(e.target.value)}
                rows={3}
                placeholder="Wpisz stałą formułę opisującą wykonanie tego etapu…"
              />
              <div className={styles.formulaActions}>
                <button className={styles.formulaSave} onClick={saveFormula}>
                  Zapisz
                </button>
                <button
                  className={styles.formulaCancel}
                  onClick={cancelFormula}
                >
                  Anuluj
                </button>
              </div>
            </div>
          ) : (
            <div
              className={`${styles.formulaText} ${
                !step.documentFormula ? styles.formulaEmpty : ""
              }`}
              onClick={startEdit}
              title="Kliknij, aby edytować formułę"
            >
              {step.documentFormula || "Brak formuły — kliknij, aby dodać"}
            </div>
          )}
        </div>

        {step.conditions.length > 0 && (
          <div className={styles.conditionsSection}>
            <div className={styles.conditionsTitle}>
              Reguły przypisania wg kwoty
            </div>
            {step.conditions.map((c) => {
              const r = ROLES[c.assignRole];
              const hasAssignees = c.assignees && c.assignees.length > 0;
              const chipLabel = hasAssignees
                ? c.assignees.length === 1
                  ? c.assignees[0].name
                  : `${c.assignees.length} stanowisk`
                : r?.label ?? c.assignRole;
              const chipColor = hasAssignees
                ? "#5d49c7"
                : r?.color ?? "#9e9e9e";
              const chipBg = hasAssignees ? "#ede8f8" : r?.bg ?? "#f5f5f5";
              const modeLabel =
                c.conditionApprovalMode === "all"
                  ? " · kontrasygnata"
                  : c.conditionApprovalMode === "two_or_more"
                  ? " · ≥ 2 osoby"
                  : "";
              return (
                <div key={c.id} className={styles.conditionRow}>
                  <div
                    className={styles.conditionBullet}
                    style={{ background: chipColor }}
                  />
                  <span className={styles.conditionAmount}>
                    {formatConditionAmount(c.value, c.operator)}
                  </span>
                  <span className={styles.conditionArrow}>→</span>
                  <span
                    className={styles.roleChip}
                    style={{
                      color: chipColor,
                      borderColor: chipColor,
                      background: chipBg,
                    }}
                  >
                    {chipLabel}
                    {modeLabel}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {step.conditions.length === 0 ? (
          /* Prosta akceptacja — trzy wiersze: osoby (stanowiska), tryb, akcje */
          <div className={styles.stepInfo}>
            <div className={styles.stepInfoRow}>
              <span className={styles.stepInfoLabel}>Osoby</span>
              <div className={styles.stepInfoChips}>
                {step.assignees.length === 0 ? (
                  <span className={styles.stepInfoMuted}>
                    — brak przypisania —
                  </span>
                ) : (
                  step.assignees.map((a) => (
                    <span key={a.id} className={styles.positionChip}>
                      {positionLabel(a)}
                    </span>
                  ))
                )}
              </div>
            </div>
            <div className={styles.stepInfoRow}>
              <span className={styles.stepInfoLabel}>Tryb akceptacji</span>
              <div className={styles.stepInfoChips}>
                <span className={styles.modeChip}>{approvalModeLabel}</span>
              </div>
            </div>
            <div className={styles.stepInfoRow}>
              <span className={styles.stepInfoLabel}>Akcje</span>
              <div className={styles.stepInfoChips}>
                {step.actions.map((a) => (
                  <span key={a} className={`${styles.actionChip} ${styles[a]}`}>
                    {ACTION_LABELS[a]}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* Wariantowa akceptacja — osoby/tryb prezentowane w sekcji warunków powyżej */
          <div className={styles.stepInfo}>
            <div className={styles.stepInfoRow}>
              <span className={styles.stepInfoLabel}>Akcje</span>
              <div className={styles.stepInfoChips}>
                {step.actions.map((a) => (
                  <span key={a} className={`${styles.actionChip} ${styles[a]}`}>
                    {ACTION_LABELS[a]}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Strzałka łącząca kroki ─────────────────────────────────────────

function Connector({ onAdd }: { onAdd: () => void }) {
  return (
    <div className={styles.connector}>
      <div className={styles.connectorLine} />
      <div className={styles.connectorArrow} />
      <Tooltip title="Dodaj etap w tym miejscu">
        <IconButton size="small" className={styles.addStepBtn} onClick={onAdd}>
          <AddIcon style={{ width: 16, height: 16 }} />
        </IconButton>
      </Tooltip>
    </div>
  );
}

// ── Diagram procesu ────────────────────────────────────────────────

function WorkflowFlow({
  template,
  onFormulaChange,
  onAddStep,
  onEditStep,
  onDeleteStep,
}: {
  template: WorkflowTemplate;
  onFormulaChange: (stepId: string, formula: string) => void;
  onAddStep: (insertAt: number) => void;
  onEditStep: (stepId: string) => void;
  onDeleteStep: (stepId: string) => void;
}) {
  const notify = template.notifyOnComplete ?? [];

  return (
    <div className={styles.flow}>
      {/* Etap 0 — Dane podstawowe (poprzedza start workflow) */}
      <div className={styles.basicCard}>
        <div className={styles.basicCardHeader}>Etap 0 · Dane podstawowe</div>
        <div className={styles.basicCardBody}>
          <div className={styles.basicRow}>
            <span className={styles.basicLabel}>Nazwa</span>
            <span className={styles.basicValue}>{template.name || "—"}</span>
          </div>
          <div className={styles.basicRow}>
            <span className={styles.basicLabel}>Dotyczy</span>
            <span className={styles.basicValue}>
              {DOC_TYPE_LABELS[template.documentType] ||
                template.documentType ||
                "—"}
            </span>
          </div>
          <div className={styles.basicRow}>
            <span className={styles.basicLabel}>Wykonanie</span>
            <span className={styles.basicValue}>
              {!template.documentType
                ? "—"
                : isEngineSupported(template.documentType)
                ? template.isActive
                  ? "Silnik aktywny — nowe zaangażowania wchodzą w ten obieg"
                  : "Silnik gotowy — workflow nieaktywny (aktywuj chipem u góry)"
                : "Tylko definicja — silnik nie wykonuje jeszcze tego dokumentu"}
            </span>
          </div>
          <div className={styles.basicRow}>
            <span className={styles.basicLabel}>Informuj o zakończeniu</span>
            <span className={styles.basicValue}>
              {notify.length > 0 ? notify.map((a) => a.name).join(", ") : "—"}
            </span>
          </div>
          {template.description && (
            <div className={styles.basicRow}>
              <span className={styles.basicLabel}>Opis</span>
              <span className={styles.basicValue}>{template.description}</span>
            </div>
          )}
        </div>
      </div>

      {/* Łącznik do startu (bez przycisku dodawania etapu) */}
      <div className={styles.connector}>
        <div className={styles.connectorLine} />
        <div className={styles.connectorArrow} />
      </div>

      <div className={`${styles.terminalNode} ${styles.start}`}>START</div>

      {template.steps.map((step, i) => (
        <div key={step.id} style={{ width: "100%" }}>
          <Connector onAdd={() => onAddStep(i)} />
          <StepCard
            step={step}
            index={i}
            onFormulaChange={onFormulaChange}
            onEdit={onEditStep}
            onDelete={onDeleteStep}
          />
        </div>
      ))}

      <Connector onAdd={() => onAddStep(template.steps.length)} />
      <div className={`${styles.terminalNode} ${styles.end}`}>KONIEC</div>
    </div>
  );
}

// ── Specjalne pozycje systemowe ───────────────────────────────────

const DIRECT_SUPERVISOR_ID = "__direct_supervisor__";
const REQUESTER_ID = "__requester__";

const DIRECT_SUPERVISOR_ENTRY: OrgPersonEntry = {
  id: DIRECT_SUPERVISOR_ID,
  name: "Bezpośredni przełożony",
  position: "Automatycznie wyznaczana rola",
  unit: "",
  memberStatus: "aktywny",
};

const REQUESTER_ENTRY: OrgPersonEntry = {
  id: REQUESTER_ID,
  name: "Osoba składająca wniosek",
  position: "Samoakceptacja — bez dodatkowego zatwierdzenia",
  unit: "",
  memberStatus: "aktywny",
};

const SPECIAL_IDS = new Set([DIRECT_SUPERVISOR_ID, REQUESTER_ID]);

// ── Helper: etykieta stanowiska (z jednostką jeśli powtórzone) ────

function buildPositionLabel(
  person: OrgPersonEntry,
  allPeople: OrgPersonEntry[],
): string {
  if (person.id === DIRECT_SUPERVISOR_ID) return "Bezpośredni przełożony";
  if (person.id === REQUESTER_ID) return "Osoba składająca wniosek";
  const real = allPeople.filter((p) => !SPECIAL_IDS.has(p.id));
  const duplicated =
    real.filter((p) => p.position === person.position).length > 1;
  return duplicated && person.unit
    ? `${person.position} — ${person.unit}`
    : person.position;
}

// Konwersja wybranych osób (OrgPersonEntry) na Assignee[] (np. do zapisu w szablonie).
const peopleToAssignees = (people: OrgPersonEntry[]): Assignee[] =>
  people.map((p, i) => {
    const isSup = p.id === DIRECT_SUPERVISOR_ID;
    const label = isSup
      ? "Bezpośredni przełożony"
      : p.unit
      ? `${p.position} — ${p.unit}`
      : p.position;
    const initials = isSup
      ? "↑"
      : label
          .split(/[\s—]+/)
          .map((w) => w[0])
          .join("")
          .slice(0, 2)
          .toUpperCase();
    return {
      id: p.id,
      name: label,
      initials,
      color: ASSIGNEE_COLORS[i % ASSIGNEE_COLORS.length],
    };
  });

// ── Multi-select "Kto wykonuje" ───────────────────────────────────

function PersonMultiSelect({
  selected,
  onChange,
}: {
  selected: OrgPersonEntry[];
  onChange: (people: OrgPersonEntry[]) => void;
}) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const allPeople = useMemo(() => {
    const positions = getOrgChartPositions().sort((a, b) =>
      a.position.localeCompare(b.position, "pl"),
    );
    return [REQUESTER_ENTRY, DIRECT_SUPERVISOR_ENTRY, ...positions];
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return allPeople;
    const q = search.toLowerCase();
    return allPeople.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.position.toLowerCase().includes(q) ||
        p.unit.toLowerCase().includes(q),
    );
  }, [allPeople, search]);

  const isSelected = (p: OrgPersonEntry) => selected.some((s) => s.id === p.id);

  const toggle = (p: OrgPersonEntry) => {
    if (isSelected(p)) {
      onChange(selected.filter((s) => s.id !== p.id));
    } else {
      onChange([...selected, p]);
    }
  };

  const remove = (id: string) => onChange(selected.filter((s) => s.id !== id));

  // zamknij po kliknięciu poza
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Po zamknięciu dropdown czyść frazę wyszukiwania (każda ścieżka zamknięcia).
  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  return (
    <div className={styles.personSelect} ref={wrapRef}>
      {/* Wyszukiwarka + toggle */}
      <div
        className={`${styles.personSelectInput} ${
          open ? styles.personSelectInputOpen : ""
        }`}
        onClick={() => setOpen((v) => !v)}
      >
        <input
          className={styles.personSearchInput}
          placeholder={
            selected.length === 0
              ? "Wyszukaj osobę lub stanowisko…"
              : `Wybrano: ${selected.length}`
          }
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
        />
        {open ? (
          <span
            className={styles.personSelectArrow}
            title="Zamknij"
            style={{
              cursor: "pointer",
              fontSize: 16,
              fontWeight: 700,
              color: "#5d49c7",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
          >
            ✕
          </span>
        ) : (
          <span className={styles.personSelectArrow}>▼</span>
        )}
      </div>

      {/* Wybrane stanowiska (chipy) */}
      {selected.length > 0 && (
        <div className={styles.personChips}>
          {selected.map((p) => {
            const isSupervisor = p.id === DIRECT_SUPERVISOR_ID;
            const isRequester = p.id === REQUESTER_ID;
            const posLabel = buildPositionLabel(p, allPeople);
            const initials = isSupervisor
              ? "↑"
              : isRequester
              ? "W"
              : posLabel
                  .split(/[\s—]+/)
                  .map((w) => w[0])
                  .join("")
                  .slice(0, 2)
                  .toUpperCase();
            const avatarColor = isSupervisor
              ? "#1565c0"
              : isRequester
              ? "#2e7d32"
              : undefined;
            return (
              <span
                key={p.id}
                className={`${styles.personChip} ${
                  isSupervisor || isRequester ? styles.personChipSupervisor : ""
                }`}
              >
                <span
                  className={styles.personChipAvatar}
                  style={avatarColor ? { background: avatarColor } : undefined}
                >
                  {initials}
                </span>
                <span className={styles.personChipName}>{posLabel}</span>
                <button
                  className={styles.personChipRemove}
                  onClick={() => remove(p.id)}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Lista dropdown */}
      {open && (
        <div className={styles.personDropdown}>
          {filtered.length === 0 ? (
            <div className={styles.personDropdownEmpty}>Brak wyników</div>
          ) : (
            filtered.map((p) => {
              const isSupervisor = p.id === DIRECT_SUPERVISOR_ID;
              const isRequester = p.id === REQUESTER_ID;
              const isSpecial = isSupervisor || isRequester;
              const posLabel = isSpecial ? p.name : p.position;
              const isDuplicate =
                !isSpecial &&
                allPeople.filter(
                  (x) => !SPECIAL_IDS.has(x.id) && x.position === p.position,
                ).length > 1;
              const subLabel = isSupervisor
                ? "Kierownik jednostki inicjatora procesu"
                : isRequester
                ? "Samoakceptacja — bez dodatkowego zatwierdzenia"
                : isDuplicate && p.unit
                ? p.unit
                : "";
              const checked = isSelected(p);
              return (
                <label
                  key={p.id}
                  className={`${styles.personOption} ${
                    checked ? styles.personOptionSelected : ""
                  } ${isSpecial ? styles.personOptionSupervisor : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(p)}
                    style={{ accentColor: "#5d49c7" }}
                  />
                  <span className={styles.personOptionInfo}>
                    <span className={styles.personOptionName}>{posLabel}</span>
                    {subLabel && (
                      <span className={styles.personOptionPos}>{subLabel}</span>
                    )}
                  </span>
                </label>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ── Typ roboczy warunku kwotowego ─────────────────────────────────

type ConditionDraft = {
  id: string;
  operator: ConditionOperator;
  amount: string;
  amountTo: string;
  people: OrgPersonEntry[];
  mode: ApprovalMode;
};

// ── Formularz nowego etapu ────────────────────────────────────────

function NewStepForm({
  insertAt,
  totalSteps,
  onSave,
  onCancel,
  initialStep,
}: {
  insertAt: number;
  totalSteps: number;
  onSave: (step: Omit<WorkflowStep, "id" | "order">) => void;
  onCancel: () => void;
  initialStep?: WorkflowStep;
}) {
  const isEditing = !!initialStep;
  const [name, setName] = useState(initialStep?.name ?? "");
  const [description, setDescription] = useState(
    initialStep?.description ?? "",
  );
  const [formulaDraft, setFormulaDraft] = useState(
    initialStep?.documentFormula ?? "",
  );
  const [showDescription, setShowDescription] = useState(
    !!initialStep?.description,
  );
  const [selectedPeople, setSelectedPeople] = useState<OrgPersonEntry[]>(() =>
    initialStep ? assigneesToOrgPeople(initialStep.assignees) : [],
  );
  const [actions, setActions] = useState<StepAction[]>(
    initialStep?.actions ?? ["approve", "reject"],
  );
  const [approvalMode, setApprovalMode] = useState<ApprovalMode>(
    initialStep?.approvalMode ?? "any",
  );
  const [conditionDrafts, setConditionDrafts] = useState<ConditionDraft[]>(() =>
    initialStep ? conditionsToDrafts(initialStep.conditions) : [],
  );

  const addCondition = () =>
    setConditionDrafts((prev) => [
      ...prev,
      {
        id: `cd-${Date.now()}`,
        operator: "lt",
        amount: "",
        amountTo: "",
        people: [],
        mode: "any",
      },
    ]);

  const handleVariantToggle = () => {
    if (conditionDrafts.length > 0) {
      setConditionDrafts([]);
    } else {
      addCondition();
    }
  };

  const removeCondition = (id: string) =>
    setConditionDrafts((prev) => prev.filter((c) => c.id !== id));

  const updateCondition = (id: string, patch: Partial<ConditionDraft>) =>
    setConditionDrafts((prev) =>
      prev.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    );

  const posLabel =
    insertAt === 0
      ? "na początku procesu"
      : insertAt === totalSteps
      ? "na końcu procesu"
      : `po etapie ${insertAt}`;

  const toggleAction = (a: StepAction) =>
    setActions((prev) =>
      prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a],
    );

  const buildAssignees = (people: OrgPersonEntry[]): Assignee[] =>
    people.map((p, i) => {
      const isSup = p.id === DIRECT_SUPERVISOR_ID;
      const label = isSup
        ? "Bezpośredni przełożony"
        : p.unit
        ? `${p.position} — ${p.unit}`
        : p.position;
      const initials = isSup
        ? "↑"
        : label
            .split(/[\s—]+/)
            .map((w) => w[0])
            .join("")
            .slice(0, 2)
            .toUpperCase();
      return {
        id: p.id,
        name: label,
        initials,
        color: ASSIGNEE_COLORS[i % ASSIGNEE_COLORS.length],
      };
    });

  const handleSave = () => {
    if (!name.trim() || actions.length === 0) return;
    const assignees = buildAssignees(selectedPeople);
    const conditions: WorkflowCondition[] = conditionDrafts
      .filter((cd) => cd.amount.trim() !== "")
      .map((cd) => {
        const amt = parseFloat(cd.amount.replace(/\s/g, "").replace(",", "."));
        const amtTo = parseFloat(
          cd.amountTo.replace(/\s/g, "").replace(",", "."),
        );
        const value: number | [number, number] =
          cd.operator === "between" && !isNaN(amtTo) ? [amt, amtTo] : amt;
        return {
          id: `cond-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          operator: cd.operator,
          value,
          assignRole: cd.people[0]?.position ?? "",
          assignees: buildAssignees(cd.people),
          conditionApprovalMode: cd.mode,
        };
      });
    const role = selectedPeople[0]?.position ?? "OTHER";
    onSave({
      name: name.trim(),
      description: description.trim(),
      role,
      actions,
      conditions,
      assignees,
      approvalMode,
      documentFormula: formulaDraft.trim(),
    });
  };

  return (
    <div className={styles.newStepForm}>
      <div className={styles.newStepFormHeader}>
        <span className={styles.newStepFormTitle}>
          {isEditing ? "Edycja etapu" : "Nowy etap"}
        </span>
        {!isEditing && (
          <span className={styles.newStepFormPos}>· {posLabel}</span>
        )}
      </div>

      <div className={styles.newWorkflowField}>
        <div className={styles.approvalModeHeaderRow}>
          <label className={styles.newWorkflowLabel}>Nazwa etapu</label>
          {!showDescription && (
            <button
              className={styles.addConditionInlineBtn}
              onClick={() => setShowDescription(true)}
            >
              + Dodaj opis
            </button>
          )}
        </div>
        <input
          autoFocus
          className={styles.newWorkflowInput}
          placeholder="np. Weryfikacja formalna"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSave()}
        />
      </div>

      {showDescription && (
        <div className={styles.newWorkflowField}>
          <label className={styles.newWorkflowLabel}>Opis</label>
          <textarea
            autoFocus
            className={styles.newWorkflowTextarea}
            placeholder="Krótki opis czynności w tym etapie…"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
        </div>
      )}

      <div className={styles.newWorkflowField}>
        <label className={styles.newWorkflowLabel}>Formuła do metryki</label>
        <textarea
          className={styles.newWorkflowTextarea}
          placeholder="Tekst wstawiany do metryki wykonania procedury…"
          value={formulaDraft}
          onChange={(e) => setFormulaDraft(e.target.value)}
          rows={2}
        />
      </div>

      {/* Przełącznik trybu akceptacji */}
      <div className={styles.variantSwitchRow}>
        <FormControlLabel
          control={
            <Switch
              checked={conditionDrafts.length > 0}
              onChange={handleVariantToggle}
              size="small"
              sx={{
                "& .MuiSwitch-thumb": {},
                "& .Mui-checked": { color: "#5d49c7" },
                "& .Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#5d49c7",
                },
              }}
            />
          }
          label={
            <span className={styles.variantSwitchLabel}>
              Akceptacja wariantowa
            </span>
          }
        />
      </div>

      {/* Tryb prosty: Kto akceptuje + Tryb zatwierdzenia */}
      {conditionDrafts.length === 0 && (
        <>
          <div className={styles.newWorkflowField}>
            <label className={styles.newWorkflowLabel}>Kto akceptuje</label>
            <PersonMultiSelect
              selected={selectedPeople}
              onChange={setSelectedPeople}
            />
          </div>

          <div className={styles.newWorkflowField}>
            <label className={styles.newWorkflowLabel}>
              Tryb zatwierdzenia
            </label>
            <select
              className={styles.newWorkflowSelect}
              value={approvalMode}
              onChange={(e) => setApprovalMode(e.target.value as ApprovalMode)}
            >
              <option value="any">Dowolna osoba</option>
              <option value="two_or_more">Co najmniej dwie osoby</option>
              <option value="all">Wszyscy (kontrasygnata)</option>
            </select>
          </div>
        </>
      )}

      {/* Tryb wariantowy: blok warunków */}
      {conditionDrafts.length > 0 && (
        <div className={styles.conditionsBuilder}>
          {conditionDrafts.map((cd, idx) => (
            <div key={cd.id} className={styles.conditionItem}>
              <div className={styles.conditionItemHeader}>
                <span className={styles.conditionItemLabel}>
                  Wariant {idx + 1}
                </span>
                <button
                  className={styles.conditionItemDelete}
                  onClick={() => removeCondition(cd.id)}
                >
                  Usuń
                </button>
              </div>

              <div className={styles.newWorkflowField}>
                <label className={styles.newWorkflowLabel}>Kwota</label>
                <div className={styles.conditionAmountRow}>
                  <select
                    className={styles.conditionOperator}
                    value={cd.operator}
                    onChange={(e) =>
                      updateCondition(cd.id, {
                        operator: e.target.value as ConditionOperator,
                      })
                    }
                  >
                    <option value="lt">poniżej</option>
                    <option value="lte">do (włącznie)</option>
                    <option value="gt">powyżej</option>
                    <option value="gte">od (włącznie)</option>
                    <option value="eq">równa</option>
                    <option value="between">między</option>
                  </select>
                  <input
                    type="text"
                    inputMode="decimal"
                    className={styles.conditionAmountInput}
                    style={{ textAlign: "right" }}
                    placeholder="0,00"
                    value={cd.amount}
                    onChange={(e) =>
                      updateCondition(cd.id, { amount: e.target.value })
                    }
                    onFocus={() =>
                      updateCondition(cd.id, {
                        amount: unformatAmount(cd.amount),
                      })
                    }
                    onBlur={() =>
                      updateCondition(cd.id, {
                        amount: formatFinancialAmount(cd.amount),
                      })
                    }
                  />
                  {cd.operator === "between" && (
                    <>
                      <span className={styles.conditionAmountSep}>a</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        className={styles.conditionAmountInput}
                        style={{ textAlign: "right" }}
                        placeholder="0,00"
                        value={cd.amountTo}
                        onChange={(e) =>
                          updateCondition(cd.id, { amountTo: e.target.value })
                        }
                        onFocus={() =>
                          updateCondition(cd.id, {
                            amountTo: unformatAmount(cd.amountTo),
                          })
                        }
                        onBlur={() =>
                          updateCondition(cd.id, {
                            amountTo: formatFinancialAmount(cd.amountTo),
                          })
                        }
                      />
                    </>
                  )}
                  <span className={styles.conditionAmountUnit}>PLN</span>
                </div>
              </div>

              <div className={styles.newWorkflowField}>
                <label className={styles.newWorkflowLabel}>Akceptujący</label>
                <PersonMultiSelect
                  selected={cd.people}
                  onChange={(people) => updateCondition(cd.id, { people })}
                />
              </div>

              <div
                className={styles.newWorkflowField}
                style={{ marginBottom: 0 }}
              >
                <label className={styles.newWorkflowLabel}>
                  Tryb zatwierdzenia
                </label>
                <select
                  className={styles.newWorkflowSelect}
                  value={cd.mode}
                  onChange={(e) =>
                    updateCondition(cd.id, {
                      mode: e.target.value as ApprovalMode,
                    })
                  }
                >
                  <option value="any">Dowolna osoba</option>
                  <option value="two_or_more">Co najmniej dwie osoby</option>
                  <option value="all">Wszyscy (kontrasygnata)</option>
                </select>
              </div>
            </div>
          ))}

          <button
            className={styles.addConditionInlineBtn}
            style={{ marginTop: 8 }}
            onClick={addCondition}
          >
            + Dodaj kolejny wariant
          </button>
        </div>
      )}

      <div className={styles.newWorkflowField}>
        <label className={styles.newWorkflowLabel}>Dostępne akcje</label>
        <div className={styles.actionsCheckboxes}>
          {(Object.entries(ACTION_LABELS) as [StepAction, string][]).map(
            ([a, label]) => (
              <label key={a} className={styles.actionCheckLabel}>
                <input
                  type="checkbox"
                  checked={actions.includes(a)}
                  onChange={() => toggleAction(a)}
                  style={{ accentColor: "#5d49c7" }}
                />
                {label}
              </label>
            ),
          )}
        </div>
      </div>

      <div className={styles.newWorkflowActions}>
        <button className={styles.newWorkflowCancel} onClick={onCancel}>
          Anuluj
        </button>
        <button
          className={styles.newWorkflowSave}
          onClick={handleSave}
          disabled={!name.trim() || actions.length === 0}
        >
          {isEditing ? "Zapisz zmiany" : "Zapisz"}
        </button>
      </div>
    </div>
  );
}

// ── Formularz nowego workflow ─────────────────────────────────────

function NewWorkflowForm({
  onCreate,
  onCancel,
  initialValues,
  onUpdate,
}: {
  onCreate?: (
    name: string,
    documentType: string,
    description: string,
    generateMetric: boolean,
    notifyOnComplete: Assignee[],
  ) => void;
  onCancel: () => void;
  initialValues?: {
    name: string;
    documentType: string;
    description: string;
    generateMetric: boolean;
    notifyOnComplete?: Assignee[];
  };
  onUpdate?: (
    name: string,
    documentType: string,
    description: string,
    generateMetric: boolean,
    notifyOnComplete: Assignee[],
  ) => void;
}) {
  const isEditing = !!initialValues;
  const [name, setName] = useState(initialValues?.name ?? "");
  // W select trzymamy NAZWĘ dokumentu z katalogu; przy zapisie zamieniamy ją na klucz
  // systemowy (np. „Zaangażowanie” → "engagement"), po którym silnik BE dobiera szablon.
  const [docType, setDocType] = useState(
    initialValues?.documentType ? docNameForSystemType(initialValues.documentType) : "",
  );
  const engineRuns = docType !== "" && isEngineSupported(docType);
  const [description, setDescription] = useState(
    initialValues?.description ?? "",
  );
  const [generateMetric, setGenerateMetric] = useState(
    initialValues?.generateMetric ?? false,
  );
  const [notifyPeople, setNotifyPeople] = useState<OrgPersonEntry[]>(() =>
    assigneesToOrgPeople(initialValues?.notifyOnComplete ?? []),
  );

  // Dokumenty z katalogu „Dokumenty podlegające akceptacji" (tylko aktywne).
  const { data: allCatalogDocs } = workflowService.useGetDocuments();
  const catalogDocs = useMemo(
    () => (allCatalogDocs ?? []).filter((d) => d.isActive),
    [allCatalogDocs],
  );

  const handleSubmit = () => {
    if (!name.trim()) return;
    const notify = peopleToAssignees(notifyPeople);
    const documentType = docType ? systemTypeForDocName(docType) : "";
    if (isEditing) {
      onUpdate?.(
        name.trim(),
        documentType,
        description.trim(),
        generateMetric,
        notify,
      );
    } else {
      onCreate?.(
        name.trim(),
        documentType,
        description.trim(),
        generateMetric,
        notify,
      );
    }
  };

  return (
    <div className={styles.newWorkflowForm}>
      <h2 className={styles.newWorkflowTitle}>
        {isEditing ? "Edytuj ustawienia workflow" : "Nowy workflow"}
      </h2>

      <div className={styles.newWorkflowField}>
        <label className={styles.newWorkflowLabel}>Nazwa *</label>
        <input
          autoFocus
          className={styles.newWorkflowInput}
          placeholder="np. Zatwierdzanie faktury zakupowej"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
        />
      </div>

      <div className={styles.newWorkflowField}>
        <label className={styles.newWorkflowLabel}>
          Dokument podlegający akceptacji
        </label>
        <select
          className={styles.newWorkflowSelect}
          value={docType}
          onChange={(e) => setDocType(e.target.value)}
        >
          <option value="">— wybierz dokument —</option>
          {/* Zaangażowanie budżetowe jest wykonywane przez silnik, więc jest do wyboru
              zawsze — także zanim ktoś doda je do katalogu „Dokumenty podlegające akceptacji”. */}
          {!catalogDocs.some((d) => d.name === "Zaangażowanie") && (
            <option value="Zaangażowanie">
              Zaangażowanie budżetowe (formularz aplikacji)
            </option>
          )}
          {catalogDocs.map((d) => (
            <option key={d.id} value={d.name}>
              {d.name}
            </option>
          ))}
        </select>

        {docType === "" ? (
          <span className={styles.docTypeEmpty}>
            Workflow nie jest przypisany do żadnego dokumentu
          </span>
        ) : engineRuns ? (
          <span className={styles.engineHint}>
            Wykonywany automatycznie: po aktywacji każde nowe zaangażowanie
            budżetowe (rejestr Budżet → Zaangażowania) wejdzie w ten obieg,
            a osoby z etapów dostaną pozycje w „Akceptacja i Podpis →
            Otrzymane”. W firmie działa jeden aktywny workflow dla zaangażowań.
            {!isEditing &&
              " Nowy workflow dostanie ścieżkę z Magistratu (akceptacja burmistrza → ocena zgodności z planem → akceptacja księgowa) z formułami wpisywanymi do dokumentu wniosku — przypisz osoby do etapów."}
          </span>
        ) : (
          <span className={styles.docTypeEmpty}>
            Dla tego dokumentu silnik nie ma jeszcze wykonania — workflow
            pozostaje definicją (etapy i formuły do metryki).
          </span>
        )}
      </div>

      <div className={styles.newWorkflowField}>
        <label className={styles.newWorkflowLabel}>
          Informuj o zakończeniu procesu
        </label>
        <PersonMultiSelect selected={notifyPeople} onChange={setNotifyPeople} />
        <span className={styles.docTypeEmpty}>
          Osoby informowane o pozytywnym zakończeniu procesu, które nie
          uczestniczą bezpośrednio w jego realizacji (np. bezpośredni przełożony
          wnioskodawcy, pracownik HR).
        </span>
      </div>

      <div className={styles.newWorkflowField}>
        <label className={styles.newWorkflowLabel}>Opis</label>
        <textarea
          className={styles.newWorkflowTextarea}
          placeholder="Krótki opis przeznaczenia workflow…"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={5}
        />
      </div>

      {/* Metryka wykonania procedury */}
      <label className={styles.metricToggle}>
        <input
          type="checkbox"
          className={styles.metricCheckbox}
          checked={generateMetric}
          onChange={(e) => setGenerateMetric(e.target.checked)}
        />
        <span className={styles.metricBox}>
          <span className={styles.metricTitle}>
            Generuj metrykę wykonania procedury
          </span>
          <span className={styles.metricDesc}>
            Po zakończeniu procesu zostanie wygenerowany dokument zawierający
            informacje o tym, kto i kiedy wykonał każdy etap procedury.
          </span>
        </span>
      </label>

      <div className={styles.newWorkflowActions}>
        <button
          className={styles.newWorkflowSave}
          onClick={handleSubmit}
          disabled={!name.trim()}
        >
          {isEditing ? "Zapisz zmiany" : "Utwórz workflow"}
        </button>
        <button className={styles.newWorkflowCancel} onClick={onCancel}>
          Anuluj
        </button>
      </div>
    </div>
  );
}

// ── Przebiegi szablonu (co silnik zrobił z tym workflow) ──────────

const INSTANCE_STATUS_LABELS: Record<string, string> = {
  active: "w toku",
  completed: "zaakceptowany",
  rejected: "odrzucony",
  returned: "zwrócony do poprawy",
  cancelled: "anulowany",
};

function TemplateInstances({ templateId }: { templateId: string }) {
  const { data: instances = [], isLoading } =
    workflowService.useTemplateInstances(templateId);
  const fmt = (iso: string) =>
    new Date(iso).toLocaleString("pl-PL", {
      dateStyle: "short",
      timeStyle: "short",
    });

  return (
    <div className={styles.instances}>
      <div className={styles.instancesTitle}>
        Przebiegi
        <span className={styles.instancesCount}>
          {instances.filter((i) => i.Status === "active").length} w toku ·{" "}
          {instances.length} łącznie
        </span>
      </div>
      {isLoading ? (
        <div className={styles.instancesEmpty}>Ładowanie…</div>
      ) : instances.length === 0 ? (
        <div className={styles.instancesEmpty}>
          Żadne zaangażowanie nie weszło jeszcze w ten obieg. Nowe zaangażowania
          trafiają tu automatycznie, gdy workflow jest aktywny.
        </div>
      ) : (
        <table className={styles.instancesTable}>
          <thead>
            <tr>
              <th>Dokument</th>
              <th>Wnioskodawca</th>
              <th>Kwota</th>
              <th>Etap</th>
              <th>Czeka na</th>
              <th>Status</th>
              <th>Start</th>
            </tr>
          </thead>
          <tbody>
            {instances.map((i) => (
              <tr key={i.Id}>
                <td>{i.DocumentLabel ?? i.DocumentId}</td>
                <td>{i.RequesterLabel ?? "—"}</td>
                <td className={styles.instancesNum}>
                  {i.Amount.toLocaleString("pl-PL", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                  zł
                </td>
                <td>
                  {i.Status === "active"
                    ? i.CurrentStepName || `Etap ${i.CurrentStepIndex + 1}`
                    : "—"}
                </td>
                <td>{i.PendingAssignees.join(", ") || "—"}</td>
                <td>
                  <span
                    className={`${styles.instanceStatus} ${
                      styles[`inst_${i.Status}`] ?? ""
                    }`}
                  >
                    {INSTANCE_STATUS_LABELS[i.Status] ?? i.Status}
                  </span>
                </td>
                <td>{fmt(i.CreatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Główny komponent ──────────────────────────────────────────────

const WorkflowModule = () => {
  // Stan początkowy: dawne dane z localStorage / demo jako placeholder do czasu
  // załadowania z BE; po odpowiedzi backend jest jedynym źródłem prawdy.
  const [templates, setTemplates] = useState<WorkflowTemplate[]>(
    () => loadSavedTemplates() ?? DEMO_TEMPLATES,
  );
  const [selectedId, setSelectedId] = useState<string>(
    () => (loadSavedTemplates() ?? DEMO_TEMPLATES)[0]?.id ?? null,
  );
  const [creatingNew, setCreatingNew] = useState(false);
  const [addingStepAt, setAddingStepAt] = useState<number | null>(null);
  const [editingWorkflow, setEditingWorkflow] = useState(false);
  const [editingStepId, setEditingStepId] = useState<string | null>(null);

  // Jedna firma na użytkownika, bez przełącznika kontekstu (decyzja D1) — nie ma już
  // zdarzenia "zmiana firmy" w trakcie sesji, więc czyszczenie wybranego szablonu/stanu
  // edycji po takiej zmianie jest zbędne (dawne useOnProfileChange nigdy by się tu nie odpaliło).

  // Osoby do przypisania = członkowie zespołu z BE (patrz assigneePeople.ts).
  const { data: workspaceData } = workspaceService.useGetWorkspace(getWorkspaceId() ?? "");
  const [peopleReady, setPeopleReady] = useState(false);
  useEffect(() => {
    setAssigneePeople(membersToAssigneePeople(workspaceData?.data));
    setPeopleReady(!!workspaceData?.data);
  }, [workspaceData]);

  const { data: serverTemplates } = workflowService.useGetTemplates();
  const createTemplate = workflowService.useCreateTemplate();
  const updateTemplate = workflowService.useUpdateTemplate();
  const deleteTemplate = workflowService.useDeleteTemplate();

  // Po załadowaniu z BE — zastąp lokalny stan i przelicz zaznaczenie.
  useEffect(() => {
    if (!serverTemplates) return;
    setTemplates(serverTemplates);
    setSelectedId((cur) =>
      serverTemplates.some((t) => t.id === cur)
        ? cur
        : serverTemplates[0]?.id ?? null,
    );
  }, [serverTemplates]);

  // Zapisuje zmodyfikowany szablon (wybrany) do BE.
  const persistUpdate = (t: WorkflowTemplate) => {
    if (t.id) updateTemplate.mutate(t);
  };

  // Modyfikuje wybrany szablon lokalnie i utrwala zmianę w BE.
  const updateSelected = (mut: (t: WorkflowTemplate) => WorkflowTemplate) => {
    const current = templates.find((t) => t.id === selectedId);
    if (!current) return;
    const next = mut(current);
    setTemplates((prev) => prev.map((t) => (t.id === selectedId ? next : t)));
    persistUpdate(next);
  };

  const handleFormulaChange = (stepId: string, formula: string) => {
    updateSelected((t) => ({
      ...t,
      steps: t.steps.map((s) =>
        s.id === stepId ? { ...s, documentFormula: formula } : s,
      ),
    }));
  };

  const handleNewWorkflow = () => {
    setCreatingNew(true);
    setSelectedId(null);
  };

  const handleCreate = (
    name: string,
    documentType: string,
    description: string,
    generateMetric: boolean,
    notifyOnComplete: Assignee[],
  ) => {
    // Zaangażowanie: startujemy od ścieżki z Magistratu (ItemAcceptationSequence) —
    // 3 etapy z formułami do dokumentu wniosku; osoby uzupełnia autor szablonu.
    const presetSteps = isEngineSupported(docNameForSystemType(documentType))
      ? MAGISTRAT_ENGAGEMENT_STEPS.map((st) => ({ ...st, conditions: [], assignees: [] }))
      : [];
    const newTemplate: WorkflowTemplate = {
      id: "",
      name,
      documentType,
      description,
      isActive: false,
      generateMetric,
      notifyOnComplete,
      steps: presetSteps,
    };
    createTemplate.mutate(newTemplate, {
      onSuccess: (saved) => {
        setTemplates((prev) => [...prev, saved]);
        setSelectedId(saved.id);
      },
    });
    setCreatingNew(false);
  };

  const handleCancelNew = () => {
    setCreatingNew(false);
    setSelectedId(templates[0]?.id ?? null);
  };

  const handleToggleActive = () => {
    updateSelected((t) => ({ ...t, isActive: !t.isActive }));
  };

  const handleDeleteWorkflow = () => {
    if (
      !window.confirm(
        "Czy na pewno chcesz usunąć ten workflow? Operacja jest nieodwracalna.",
      )
    )
      return;
    const idToDelete = selectedId;
    if (idToDelete) deleteTemplate.mutate(idToDelete);
    setTemplates((prev) => {
      const next = prev.filter((t) => t.id !== idToDelete);
      setSelectedId(next[0]?.id ?? null);
      setEditingWorkflow(false);
      return next;
    });
  };

  const handleUpdateWorkflow = (
    name: string,
    documentType: string,
    description: string,
    generateMetric: boolean,
    notifyOnComplete: Assignee[],
  ) => {
    updateSelected((t) => ({
      ...t,
      name,
      documentType,
      description,
      generateMetric,
      notifyOnComplete,
    }));
    setEditingWorkflow(false);
  };

  const handleAddStep = (insertAt: number) => {
    setEditingStepId(null);
    setAddingStepAt(insertAt);
  };

  const handleEditStep = (stepId: string) => {
    setAddingStepAt(null);
    setEditingStepId(stepId);
  };

  const handleDeleteStep = (stepId: string) => {
    updateSelected((t) => ({
      ...t,
      steps: t.steps
        .filter((s) => s.id !== stepId)
        .map((s, i) => ({ ...s, order: i + 1 })),
    }));
  };

  const handleSaveStep = (stepData: Omit<WorkflowStep, "id" | "order">) => {
    const at = addingStepAt ?? 0;
    updateSelected((t) => {
      const newStep: WorkflowStep = {
        ...stepData,
        id: `s-${Date.now()}`,
        order: at + 1,
      };
      const steps = [...t.steps];
      steps.splice(at, 0, newStep);
      return { ...t, steps: steps.map((s, i) => ({ ...s, order: i + 1 })) };
    });
    setAddingStepAt(null);
  };

  const handleSaveEditedStep = (
    stepData: Omit<WorkflowStep, "id" | "order">,
  ) => {
    updateSelected((t) => ({
      ...t,
      steps: t.steps.map((s) =>
        s.id === editingStepId ? { ...s, ...stepData } : s,
      ),
    }));
    setEditingStepId(null);
  };

  const selected = templates.find((t) => t.id === selectedId) ?? null;
  const [showDesc, setShowDesc] = useState(false);
  // Podział na zakładki: lista szablonów / kreator szablonów.
  const [subTab, setSubTab] = useState<"list" | "builder">("list");

  return (
    // key: podkomponenty cache'ują listę osób w useMemo([]) — po dociągnięciu firmy remontujemy raz.
    <div className={styles.workflowWrap} key={peopleReady ? "people-ready" : "people-loading"}>
      <div className={styles.subTabs}>
        <div
          className={`${styles.subTab} ${
            subTab === "list" ? styles.subTabActive : ""
          }`}
          onClick={() => setSubTab("list")}
        >
          Dokumenty podlegające akceptacji
        </div>
        <div
          className={`${styles.subTab} ${
            subTab === "builder" ? styles.subTabActive : ""
          }`}
          onClick={() => setSubTab("builder")}
        >
          Kreator szablonów
        </div>
      </div>

      <div className={styles.tabContent}>
        {subTab === "list" && <DocumentsCatalog />}

        {subTab === "builder" && (
          /* Zakładka „Kreator szablonów" – lista szablonów + edytor workflow */
          <div className={styles.root}>
            <div className={styles.sidebar}>
              <div className={styles.sidebarHeader}>
                <h2>Szablony workflow</h2>
              </div>

              <div className={styles.templateList}>
                <button
                  className={styles.newWorkflowBtn}
                  onClick={handleNewWorkflow}
                >
                  <AddIcon style={{ width: 15, height: 15 }} />
                  Nowy workflow
                </button>

                {templates.map((t) => (
                  <div
                    key={t.id}
                    className={`${styles.templateItem} ${
                      t.id === selectedId ? styles.active : ""
                    }`}
                    onClick={() => {
                      setSelectedId(t.id);
                      setCreatingNew(false);
                    }}
                  >
                    <div
                      className={`${styles.templateDot} ${
                        t.isActive ? styles.active : styles.inactive
                      }`}
                    />
                    <div className={styles.templateInfo}>
                      <div className={styles.templateName}>{t.name}</div>
                      <div className={styles.templateMeta}>
                        {DOC_TYPE_LABELS[t.documentType] ?? t.documentType} ·{" "}
                        {t.steps.length} etapy
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.editor}>
              {creatingNew ? (
                <div className={styles.editorBody}>
                  <NewWorkflowForm
                    onCreate={handleCreate}
                    onCancel={handleCancelNew}
                  />
                </div>
              ) : selected ? (
                <>
                  <div className={styles.editorHeader}>
                    <div className={styles.editorTitle}>
                      <div className={styles.editorTitleRow}>
                        <div className={styles.editorTitleText}>
                          {selected.name}
                        </div>
                        {selected.description && (
                          <button
                            className={styles.descToggleBtn}
                            onClick={() => setShowDesc((v) => !v)}
                            title={showDesc ? "Ukryj opis" : "Pokaż opis"}
                          >
                            {showDesc ? "▲" : "▼"}
                          </button>
                        )}
                      </div>
                      {showDesc && selected.description && (
                        <p className={styles.editorDesc}>
                          {selected.description}
                        </p>
                      )}
                    </div>
                    <div className={styles.editorActions}>
                      {isEngineSupported(selected.documentType) && (
                        <Tooltip title="Ten typ dokumentu jest wykonywany automatycznie przez silnik workflow (zaangażowania budżetowe)">
                          <span className={`${styles.statusChip} ${styles.engine}`}>
                            Silnik
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip
                        title={
                          selected.isActive
                            ? "Kliknij, aby dezaktywować"
                            : "Kliknij, aby aktywować"
                        }
                      >
                        <span
                          className={`${styles.statusChip} ${
                            selected.isActive ? styles.active : styles.inactive
                          }`}
                          onClick={handleToggleActive}
                          style={{ cursor: "pointer" }}
                        >
                          {selected.isActive ? "Aktywny" : "Nieaktywny"}
                        </span>
                      </Tooltip>
                      <Tooltip title="Edytuj ustawienia workflow">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingWorkflow(true);
                            setAddingStepAt(null);
                            setEditingStepId(null);
                          }}
                        >
                          <EditIcon style={{ width: 18, height: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Usuń workflow">
                        <IconButton size="small" onClick={handleDeleteWorkflow}>
                          <DeleteIcon style={{ width: 18, height: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>

                  <div className={styles.editorBody}>
                    {editingWorkflow ? (
                      <NewWorkflowForm
                        initialValues={{
                          name: selected.name,
                          documentType: selected.documentType,
                          description: selected.description ?? "",
                          generateMetric: selected.generateMetric ?? false,
                          notifyOnComplete: selected.notifyOnComplete ?? [],
                        }}
                        onUpdate={handleUpdateWorkflow}
                        onCancel={() => setEditingWorkflow(false)}
                      />
                    ) : editingStepId !== null ? (
                      <NewStepForm
                        insertAt={0}
                        totalSteps={selected.steps.length}
                        onSave={handleSaveEditedStep}
                        onCancel={() => setEditingStepId(null)}
                        initialStep={selected.steps.find(
                          (s) => s.id === editingStepId,
                        )}
                      />
                    ) : addingStepAt !== null ? (
                      <NewStepForm
                        insertAt={addingStepAt}
                        totalSteps={selected.steps.length}
                        onSave={handleSaveStep}
                        onCancel={() => setAddingStepAt(null)}
                      />
                    ) : selected.steps.length === 0 ? (
                      <div className={styles.emptyFlow}>
                        <p>Workflow nie ma jeszcze żadnych etapów.</p>
                        <p>
                          Użyj przycisku „+" na diagramie poniżej, aby dodać
                          pierwszy etap.
                        </p>
                        <WorkflowFlow
                          template={selected}
                          onFormulaChange={handleFormulaChange}
                          onAddStep={handleAddStep}
                          onEditStep={handleEditStep}
                          onDeleteStep={handleDeleteStep}
                        />
                      </div>
                    ) : (
                      <>
                        <WorkflowFlow
                          template={selected}
                          onFormulaChange={handleFormulaChange}
                          onAddStep={handleAddStep}
                          onEditStep={handleEditStep}
                          onDeleteStep={handleDeleteStep}
                        />
                        {isEngineSupported(selected.documentType) && (
                          <TemplateInstances templateId={selected.id} />
                        )}
                      </>
                    )}
                  </div>
                </>
              ) : (
                <div className={styles.empty}>
                  Wybierz szablon workflow z listy po lewej lub utwórz nowy.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowModule;
